import { useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListMyMentions,
  useMarkMentionsRead,
  getListMyMentionsQueryKey,
} from "@workspace/api-client-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export function MentionsInbox() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const queryKey = getListMyMentionsQueryKey();
  const { data: mentions } = useListMyMentions(undefined, {
    query: {
      queryKey,
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    },
  });
  const markRead = useMarkMentionsRead();

  const unreadCount = (mentions ?? []).filter((m) => !m.readAt).length;

  const handleOpen = (mention: {
    id: number;
    commentId: number;
    entityType: string;
    entityId: number;
    documentKind?: string | null;
    readAt?: string | null;
  }) => {
    if (!mention.readAt) {
      markRead.mutate(
        { data: { mentionIds: [mention.id] } },
        {
          onSuccess: () => queryClient.invalidateQueries({ queryKey }),
        },
      );
    }
    let target: string;
    if (mention.entityType === "coach") {
      target = `/coaches/${mention.entityId}`;
    } else if (mention.documentKind === "term_sheet") {
      target = `/term-sheets/${mention.entityId}`;
    } else {
      target = `/contracts/${mention.entityId}`;
    }
    setOpen(false);
    setLocation(target);
  };

  const markAllRead = () => {
    markRead.mutate(
      { data: { all: true } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Mentions">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold leading-none text-white bg-red-500 rounded-full px-1">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold text-sm">Mentions</div>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {!mentions || mentions.length === 0 ? (
            <div className="p-6 text-sm text-center text-muted-foreground">
              No mentions yet. Teammates can @mention you on contracts and coaches.
            </div>
          ) : (
            <ul className="divide-y">
              {mentions.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => handleOpen(m)}
                    className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${m.readAt ? "" : "bg-blue-50/40"}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-medium truncate">{m.authorName}</span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatDistanceToNow(new Date(m.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Badge variant="outline" className="text-[10px] uppercase">{m.entityType}</Badge>
                      <span className="truncate">{m.entityLabel}</span>
                    </div>
                    <div className="text-sm text-foreground/80 line-clamp-2">{m.bodyPreview}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
